import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { exchangeWebNonce, refreshAuthSession } from '../api';
import { getSafeReturnUrl } from '../utils/returnUrl';
import { AuthContext } from './authContext';
import { resetAuthExpirationNotification, subscribeToAuthExpiration } from './authEvents';
import {
  clearAuthSession,
  migrateLegacyAuthSession,
  readAuthSession,
  saveAuthSession,
} from './authStorage';
import { getSafeInternalRoute, readPortalEntry, sanitizePortalEntryUrl } from './portalEntry';
import { isTokenExpired } from './token';

const getCurrentInternalRoute = (location) => getSafeInternalRoute(`${location.pathname}${location.search}`);

const redirectDirectPathToHash = () => {
  const { pathname, search, origin } = window.location;
  if (!pathname || pathname === '/' || pathname === '/index.html' || pathname.includes('.')) return false;

  const cleanPath = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  window.location.replace(`${origin}/#${cleanPath}${search}`);
  return true;
};

export default function AuthProvider({ children }) {
  const [status, setStatus] = useState('initializing');
  const [session, setSession] = useState(null);
  const bootstrapStarted = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  const authenticate = useCallback((nextSession) => {
    const storedSession = saveAuthSession(nextSession);
    if (!storedSession) throw new Error('Unable to store the authenticated session.');
    resetAuthExpirationNotification();
    setSession(storedSession);
    setStatus('authenticated');
    return storedSession;
  }, []);

  const clearSession = useCallback(() => {
    clearAuthSession();
    setSession(null);
    setStatus('unauthenticated');
  }, []);

  const redirectToExpiredLogin = useCallback((from = getCurrentInternalRoute(location)) => {
    clearSession();
    navigate('/login', {
      replace: true,
      state: {
        authNotice: 'session_expired',
        from: getSafeInternalRoute(from),
      },
    });
  }, [clearSession, location, navigate]);

  const logout = useCallback(() => {
    clearSession();
    resetAuthExpirationNotification();
    navigate('/login', { replace: true, state: null });
  }, [clearSession, navigate]);

  useEffect(() => subscribeToAuthExpiration(() => {
    redirectToExpiredLogin();
  }), [redirectToExpiredLogin]);

  useEffect(() => {
    if (bootstrapStarted.current) return;
    bootstrapStarted.current = true;

    const bootstrap = async () => {
      if (redirectDirectPathToHash()) return;

      const entry = readPortalEntry(window.location);
      if (entry.returnUrl) {
        localStorage.setItem('returnUrl', getSafeReturnUrl(entry.returnUrl));
      }

      if (entry.nonce || entry.returnUrl) {
        try {
          const cleanUrl = sanitizePortalEntryUrl(window.location.href);
          window.history.replaceState({}, document.title, cleanUrl);
        } catch {
          // Authentication continues even if an older browser cannot clean the address bar.
        }
      }

      if (entry.nonce) {
        clearAuthSession();
        try {
          const response = await exchangeWebNonce(entry.nonce);
          const idToken = response?.idToken || response?.data?.idToken;
          if (!idToken || isTokenExpired(idToken)) throw new Error('Nonce returned an invalid token.');
          authenticate({ idToken, source: 'nonce' });
          return;
        } catch {
          redirectToExpiredLogin();
          return;
        }
      }

      if (entry.returnUrl) {
        redirectToExpiredLogin();
        return;
      }

      const storedSession = migrateLegacyAuthSession() || readAuthSession();
      if (!storedSession) {
        setStatus('unauthenticated');
        return;
      }

      if (!isTokenExpired(storedSession.idToken)) {
        setSession(storedSession);
        setStatus('authenticated');
        resetAuthExpirationNotification();
        return;
      }

      if (storedSession.refreshToken && storedSession.username) {
        try {
          const refreshedSession = await refreshAuthSession();
          setSession(refreshedSession);
          setStatus('authenticated');
          resetAuthExpirationNotification();
          return;
        } catch {
          // The shared expiration path below clears the rejected session.
        }
      }

      if (location.pathname === '/login') {
        clearSession();
      } else {
        redirectToExpiredLogin();
      }
    };

    void bootstrap();
  }, [authenticate, clearSession, location.pathname, redirectToExpiredLogin]);

  const value = useMemo(() => ({
    status,
    session,
    authenticate,
    logout,
  }), [authenticate, logout, session, status]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
